import { createRedirectController } from '../../../payment-navigation.mjs';
import axios from '../../../api-client.ts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { ThemeModeComponent } from '../../../../_mesomb/assets/ts/layout';
import { KTCard, KTCardBody } from '../../../../_mesomb/helpers';
import { useThemeMode } from '../../../../_mesomb/partials/layout/theme-mode/ThemeModeProvider.tsx';
import { AppConfig, CheckoutSessionModel } from '../../../../types';
import PayWidget from '../components/PayWidget.tsx';

const systemMode = ThemeModeComponent.getSystemMode() as 'light' | 'dark';
const checkoutPayEndpoint = (id: string) =>
  `/internal/checkout/sessions/${id}/confirm/`;
const cancelPayEndpoint = (id: string) =>
  `/internal/checkout/sessions/${id}/cancel/`;

export interface CheckoutPaymentProps {
  session?: CheckoutSessionModel;
  configs: AppConfig;
}

const CheckoutPayment = ({ session: initialSession, configs }: CheckoutPaymentProps) => {
  const [session, setSession] = useState(initialSession);
  const { t } = useTranslation();
  const { mode } = useThemeMode();
  const calculatedMode = mode === 'system' ? systemMode : mode;

  const redirectController = useMemo(() => createRedirectController(url => window.location.assign(url)), [session?.id]);
  const dialogOpen=useRef(false);
  useEffect(() => {
    redirectController.activate();
    return () => {redirectController.cancel();if(dialogOpen.current)Swal.close();};
  },[redirectController]);

  const redirectAfterPayment = useCallback(async (url: string) => {
    let countdown: number | undefined;
    try { await redirectController.run(url, async target => {
    const destination = target.external ? ` — ${target.host}` : '';
    dialogOpen.current=true;
    await Swal.fire({
      title: t('Checkout completed'),
      text: t('Redirecting in {{seconds}} seconds', { seconds: 5 }) + destination,
      icon: 'success',
      timer: 5000,
      timerProgressBar: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
      buttonsStyling: false,
      confirmButtonText: t('Redirect now'),
      customClass: { confirmButton: 'btn fw-bold btn-light-primary' },
      background: calculatedMode === 'dark' ? 'rgb(21, 23, 28)' : undefined,
      didOpen: () => {
        countdown = window.setInterval(() => {
          const seconds = Math.ceil((Swal.getTimerLeft() ?? 0) / 1000);
          const content = Swal.getHtmlContainer();
          if (content) content.textContent = t('Redirecting in {{seconds}} seconds', { seconds }) + destination;
        }, 200);
      },
      willClose: () => {window.clearInterval(countdown);dialogOpen.current=false;},
    });
    }); } catch {
      await Swal.fire({icon:'info', text:t('Payment completed, but the return URL is invalid. Please contact the merchant.')});
    }
  }, [calculatedMode, t, redirectController]);

  useEffect(() => {
    setSession(initialSession);
  }, [initialSession]);

  useEffect(() => {
    if (!session || session.status !== 'active') return;
    let stopped = false;
    let polling = false;
    const check = async () => {
      if (polling) return;
      polling = true;
      try {
        const { data } = await axios.get(`/internal/checkout/sessions/${session.id}/status/`);
        if (stopped) return;
        setSession(previous => previous ? {...previous, status:data.status, attempt_count:data.attempt_count, can_retry:data.can_retry} : previous);
        if (data.status !== 'active') {
          if (data.status === 'completed' && data.redirect) void redirectAfterPayment(data.redirect);
        }
      } catch {
        // Keep checking the existing payment when the network is unavailable.
      } finally {
        polling = false;
      }
    };
    const timer = window.setInterval(check, 3000);
    void check();
    return () => { stopped = true; window.clearInterval(timer); };
  }, [session?.id, session?.status, redirectAfterPayment]);

  const totalItems = useMemo(() => {
    if (!session) return 0;
    return session.line_items?.reduce((acc, item) => acc + item.quantity, 0);
  }, [session]);

  const normalizedStatus = session?.status?.toLowerCase();
  const terminalStatus: 'expired' | 'cancelled' | 'completed' | null =
    normalizedStatus === 'completed' || normalizedStatus === 'paid'
      ? 'completed'
      : normalizedStatus === 'cancelled' || normalizedStatus === 'canceled'
        ? 'cancelled'
        : normalizedStatus !== 'active' && (session?.is_expired || normalizedStatus === 'expired')
          ? 'expired'
          : null;

  if (!session) {
    return (
      <KTCard flush={true} className={'w-lg-700px'}>
        <KTCardBody className={'py-10 text-center'}>
          <h2 className='mb-3'>{t('Checkout unavailable')}</h2>
          <p className='text-muted mb-0'>
            {t(
              'The checkout session could not be loaded. Please verify your link or try again later.'
            )}
          </p>
        </KTCardBody>
      </KTCard>
    );
  }

  if (terminalStatus) {
    const statusTitle =
      terminalStatus === 'expired'
        ? t('Checkout expired')
        : terminalStatus === 'cancelled'
          ? t('Checkout cancelled')
          : t('Checkout completed');

    const statusMessage =
      terminalStatus === 'expired'
        ? t('This checkout session has expired. Please request a new payment link.')
        : terminalStatus === 'cancelled'
          ? t('This checkout session was cancelled and can no longer accept payments.')
          : t('This checkout session has already been completed. No further payment is required.');

    return (
      <KTCard flush={true} className={'w-lg-700px'}>
        <KTCardBody className={'py-10 text-center'}>
          <h2 className='mb-3'>{statusTitle}</h2>
          <p className='text-muted mb-0'>{statusMessage}</p>
        </KTCardBody>
      </KTCard>
    );
  }

  if (normalizedStatus === 'active') {
    return (
      <KTCard flush={true} className='w-lg-700px'>
        <KTCardBody className='py-10 text-center'>
          <span className='spinner-border text-primary mb-4' role='status' />
          <h2>{t('Payment in progress')}</h2>
          <p className='text-muted'>{t('Please validate the payment on your phone. We are checking its status.')}</p>
        </KTCardBody>
      </KTCard>
    );
  }

  return (
    <div className='d-flex flex-column flex-root'>
      <div className='d-flex flex-column flex-center flex-column-fluid'>
        <div className='d-flex flex-column p-10'>
          <PayWidget
            title={
              session.title ?? t('Checkout', { defaultValue: 'Checkout' })
            }
            description={session.description}
            subtitle={`${session.application.name}` + (totalItems ?? 0 > 0 ? ` · ${totalItems} ${t('item(s)', { defaultValue: 'item(s)' })}` : '')}
            configs={configs}
            currency={session.currency}
            defaultTotalAmount={session.amount}
            status={session.status}
            application={session.application}
            line_items={session.line_items ?? []}
            metadata={session.metadata}
            amountOption={{
              type:
                (session.line_items ?? []).length > 0 ? 'products' : 'freeType',
              editable: false,
              suggested: session.amount,
            }}
            customer={session.customer}
            options={{
              collectCustomerInfo:
                session.required_contact_info ||
                (session.custom_fields?.length ?? 0) > 0,
              customFields: session.custom_fields,
              requiredContactInfo: session.required_contact_info,
              consentCollection: session.consent_collection,
              customText: session.custom_text,
              discounts: session.discounts,
            }}
            brandingSettings={session.branding_settings}
            callToAction={session.call_to_action}
            object={'checkout'}
            onCancel={session.is_cancellable ? async () => {
              const { data } = await axios.post(cancelPayEndpoint(session.id),);
              if (data?.redirect) {
                try {await redirectController.run(data.redirect);}
                catch {await Swal.fire({icon:'info',text:t('Checkout cancelled, but the return URL is invalid. Please contact the merchant.')});}
                return;
              }
            } : undefined}
            onSubmit={async (
              values,
              { setSubmitting, setErrors, resetForm }
            ) => {
              try {
                const {data:current} = await axios.get(`/internal/checkout/sessions/${session.id}/status/`);
                setSession(previous => previous ? {...previous, status:current.status, attempt_count:current.attempt_count, can_retry:current.can_retry} : previous);
                if (current.status === 'completed') {
                  if (current.redirect) await redirectAfterPayment(current.redirect);
                  return;
                }
                if (current.status === 'active') {
                  await Swal.fire({icon:'info', text:t('Payment is being processed. Please check its status before trying again.')});
                  return;
                }
                if (['cancelled','expired'].includes(current.status) || (current.status === 'payment_failed' && !current.can_retry)) {
                  throw new Error(t('This checkout cannot accept another payment.'));
                }
                const payload = { service: values.service, payer: values.payer, country: values.country };
                const { data } = await axios.post(
                  checkoutPayEndpoint(session.id),
                  payload,
                  { mesombOperationId: `checkout:${session.id}:attempt:${current.status === 'payment_failed' ? (current.attempt_count ?? 0) + 1 : Math.max(1, current.attempt_count ?? 0)}` }
                );
                setSession({ ...session, ...data.session, status: data.status });

                if (data.status === 'completed' && data.redirect) {
                  await redirectAfterPayment(data.redirect);
                  return;
                }

                await Swal.fire({
                  title: t('Confirmation!', { defaultValue: 'Confirmation!' }),
                  text:
                    data?.message ??
                    t(
                      'Payment request sent successfully. Please validate on your phone.',
                      {
                        defaultValue:
                          'Payment request sent successfully. Please validate on your phone.',
                      }
                    ),
                  icon: data?.status === 'completed' ? 'success' : data?.status === 'active' ? 'info' : 'error',
                  buttonsStyling: false,
                  confirmButtonText: t('Ok, got it!', {
                    defaultValue: 'Ok, got it!',
                  }),
                  customClass: {
                    confirmButton: 'btn fw-bold btn-light-primary',
                  },
                  background:
                    calculatedMode == 'dark' ? 'rgb(21, 23, 28)' : undefined,
                });

                resetForm({});
              } catch (error: unknown) {
                if (axios.isAxiosError(error) && (!error.response || error.response.status >= 500)) {
                  setSession((previous) => previous ? { ...previous, status: 'active' } : previous);
                }
                const serverErrors = axios.isAxiosError(error)
                  ? error.response?.data?.errors
                  : undefined;
                if (serverErrors && typeof serverErrors === 'object') {
                  setErrors(serverErrors);
                }

                await Swal.fire({
                  title: t('Failure!', { defaultValue: 'Failure!' }),
                  text:
                    (axios.isAxiosError(error)
                      ? error.response?.data?.error?.message || error.response?.data?.detail
                      : error instanceof Error ? error.message : undefined) ??
                    t('Unable to start payment. Please try again.', {
                      defaultValue:
                        'Unable to start payment. Please try again.',
                    }),
                  icon: 'error',
                  buttonsStyling: false,
                  confirmButtonText: t('Ok, got it!', {
                    defaultValue: 'Ok, got it!',
                  }),
                  customClass: {
                    confirmButton: 'btn fw-bold btn-light-primary',
                  },
                  background:
                    calculatedMode == 'dark' ? 'rgb(21, 23, 28)' : undefined,
                });
              } finally {
                setSubmitting(false);
              }
            }}
            environment={session.environment}
          />
        </div>
      </div>
    </div>
  );
};

export default CheckoutPayment;
